// Trigger CI to pick up latest rocket-frontend-automation E2E fixes on perf-app-1.
declare global {
  interface Window {
    BUILD_ID: string;
  }
}

if (
  !process.env.REACT_APP_OKTA_ISSUER_DOMAIN ||
  !process.env.REACT_APP_PUSHER_CLIENT_KEY ||
  !process.env.REACT_APP_INTERCOM_APP_ID ||
  !process.env.REACT_APP_PUSHER_CLUSTER ||
  !process.env.REACT_APP_PDFTRON_KEY ||
  !process.env.REACT_APP_ASSETS_HOST ||
  !process.env.REACT_APP_PDFTRON_KEY ||
  !process.env.REACT_APP_ASSETS_HOST
) {
  throw new Error(
    'Ensure the env variables are setup, as stated in the README.md file.',
  );
}

/** *
 * This method constructs the api host
 * Checks for current window location path and constructs the api path in a way as:
 *    {protocol}://{subdomain}.api.{domain}
 *    Ex: https://demo.api.nitro.run
 *
 */

const getApiHost = (fallback = process.env.REACT_APP_API_HOST) => {
  const { hostname, protocol } = window.location;

  if (hostname === 'localhost') {
    return fallback;
  }

  const domainArr = hostname.split('.');
  const subdomain = domainArr[0];
  const domain = domainArr[1];

  if (process.env.PLAYWRIGHT_TESTS) {
    const subdomainFromLocalStorage = window.localStorage.getItem(
      'playwright_host_api',
    );

    if (!subdomainFromLocalStorage) {
      return fallback;
    }

    return hostname.replace(
      subdomain,
      `${protocol}//${subdomainFromLocalStorage}.api`,
    );
  }

  if (hostname?.includes('highradius-demo')) {
    return 'https://highradius.api.rocketlane.com';
  }

  if (hostname?.includes('sprinklr-demo')) {
    return 'https://sprinklr.api.rocketlane.com';
  }

  if (hostname?.includes('sprinklr-prod-new')) {
    return 'https://sprinklr.api.rocketlane.com';
  }

  if (subdomain === 'app' && ['nitro', 'rocketlane'].includes(domain)) {
    return hostname.replace(subdomain, `${protocol}//api`);
  }

  return hostname.replace(subdomain, `${protocol}//${subdomain}.api`);
};

// done because for mobile meta alone we need to send 'app', but for other requests we dont
const getApiHostForMobileMeta = (fallback = process.env.REACT_APP_API_HOST) => {
  const { hostname, protocol } = window.location;

  if (hostname === 'localhost' || process.env.PLAYWRIGHT_TESTS) {
    return fallback;
  }
  const subdomain = hostname.split('.')[0];

  return hostname.replace(subdomain, `${protocol}//${subdomain}.api`);
};

// Current API host (starts with .api subdomain); get/set for cookies update
const apiHostState = (() => {
  let value: string | undefined = getApiHost();

  getApiHost();

  return {
    get current(): string | undefined {
      return value;
    },
    set current(_trigger: boolean | void) {
      let hostname: string;
      let protocol: string;

      if (window.location.hostname === 'localhost') {
        const apiHost = value;

        if (!apiHost) {
          return;
        }
        try {
          const apiUrl = new URL(apiHost);

          hostname = apiUrl.hostname;
          protocol = apiUrl.protocol;
        } catch {
          return;
        }
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          return;
        }
      } else {
        hostname = window.location.hostname;
        protocol = window.location.protocol;
      }

      const subdomain = hostname.split('.')[0];
      const hostWithoutApi = hostname.includes('.api.')
        ? hostname.replace('.api.', '.')
        : hostname;

      value = hostWithoutApi.replace(subdomain, `${protocol}//${subdomain}`);
    },
  };
})();

/**
 * Get the current API host
 * Initially returns host with .api subdomain, can be updated after cookiesEnabled check
 */
export const getCurrentApiHost = () => apiHostState.current;

/**
 * Update API host to remove .api subdomain when cookies are enabled
 * This allows subsequent API calls to use the subdomain directly (without .api)
 * When on localhost, derive from currentApiHost (production backend) so we still strip .api.
 */
export const updateApiHostForCookies = () => {
  apiHostState.current = true;
};

export const NITRO_OAUTH_HOST = getCurrentApiHost()?.includes('nitro.run')
  ? 'https://id.nitro.run'
  : 'https://id.rocketlane.com';
export const CONVERSATION_SERVICE_URL =
  'https://conversation-service.rl-platforms.rocketlane.com';
export const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER;
export const PUSHER_CLIENT_KEY = process.env.REACT_APP_PUSHER_CLIENT_KEY;
export const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;
export const API_HOST_FOR_MOBILE_META = getApiHostForMobileMeta();
export const API_VERSION = 'v1';
export const API_NAMESPACE = 'api';
export const API_VERSION_V2 = 'v2';
export const OKTA_ACCESS_TOKEN_EXPIRY_WINDOW_IN_MINUTES = 15;
export const OKTA_ACCESS_TOKEN_SAFE_WINDOW_IN_MINUTES = 5;
export const OKTA_ACCESS_TOKEN_CHECK_WINDOW_IN_SECONDS = 10;
export const OKTA_ISSUER_DOMAIN = process.env.REACT_APP_OKTA_ISSUER_DOMAIN;

/**
 * Get clientRegion from localStorage accountMeta
 * The accountMeta is fetched via getInitData() (/home/init) and cached in localStorage
 * Returns 'eu' if clientRegion is 'eu', 'us' if 'us', or undefined if not available
 *
 * This function can be reused throughout the app for regional configuration
 */
export const getClientRegion = (): string | undefined => {
  try {
    const accountMetaStr = localStorage.getItem('accountMeta');

    if (accountMetaStr) {
      const accountMeta = JSON.parse(accountMetaStr);
      const region = accountMeta?.clientRegion;

      return region;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[getClientRegion] Failed to parse accountMeta:', error);
  }

  return undefined;
};

// Regional Marketplace URLs
// Uses clientRegion from accountMeta which is fetched via getInitData() (/home/init) and cached in localStorage
const getMarketplaceUrls = () => {
  // Get clientRegion using the reusable function
  const clientRegion = getClientRegion();

  // Route based on clientRegion from backend
  if (clientRegion === 'eu') {
    return {
      frontend: 'https://assets.marketplace.eu.rocketlane.com',
      backend: 'https://api.marketplace.eu.rocketlane.com',
    };
  }

  // Fallback to hostname check if accountMeta is not yet loaded
  // This handles the case during initial page load before getInitData() completes
  const { hostname } = window.location;

  if (
    hostname.includes('.eu.') ||
    hostname.includes('-eu.') ||
    hostname === 'kadhir.rocketlane.com' ||
    hostname.startsWith('kadhir.') ||
    hostname === 'testeu7.rocketlane.com' ||
    hostname.startsWith('testeu7.')
  ) {
    return {
      frontend: 'https://assets.marketplace.eu.rocketlane.com',
      backend: 'https://api.marketplace.eu.rocketlane.com',
    };
  }

  // Default to US region
  return {
    frontend: 'https://assets.marketplace.rocketlane.com',
    backend: 'https://api.marketplace.rocketlane.com',
  };
};

const marketplaceUrls = getMarketplaceUrls();

export const MARKETPLACE_SERVICE_FE_URL = marketplaceUrls.frontend;
export const MARKETPLACE_SERVICE_BE_URL = marketplaceUrls.backend;
export const PDFTRON_KEY = process.env.REACT_APP_PDFTRON_KEY;

export const CDN_BASE_URL = process.env.REACT_APP_ASSETS_HOST;

export const AI_STREAM_BASE_URL =
  process.env.REACT_APP_AI_STREAM_BASE_URL ||
  'https://colaslackerbot.rocketlane.com';

/**
 * Get CK_HOST based on clientRegion from accountMeta
 * If clientRegion is "eu", returns docs.eu.rocketlane.com:443
 * If clientRegion is "us" or not set, uses the existing logic (.orbits.run / .nitro.run split)
 */
export const getCKHost = (): string => {
  // Get clientRegion from localStorage accountMeta using the reusable function
  const clientRegion = getClientRegion();

  // Route based on clientRegion from backend
  if (clientRegion === 'eu') {
    return 'docs.eu.rocketlane.com:443';
  }

  // Default logic for US region or when clientRegion is not available
  if (
    window.location.origin.endsWith('.orbits.run') ||
    window.location.origin.endsWith('.nitro.run') ||
    window.location.hostname === 'localhost'
  ) {
    return 'docs-stage.nitro.run:443';
  }

  return 'docs.rocketlane.com:443';
};

export const CK_HOST = getCKHost();

export const WS_CK_HOST = CK_HOST;

export const isCKWsCloud = () => false;

export const AG_GRID_LICENSE_KEY = process.env.REACT_APP_AG_GRID_LICENSE_KEY;

const BUILD_ID = process.env.REACT_APP_BUILD_ID;

window.BUILD_ID = BUILD_ID || '';
if (process.env.NODE_ENV === 'production') {
  // eslint-disable-next-line no-console
  console.log('BUILD_ID', BUILD_ID);
}

export { BUILD_ID };
